import React from "react";
import LegalPage from "@/components/legal/LegalPage";

export default function Terms() {
  return (
    <LegalPage
      pageTitle="Terms of Service"
      slug="terms"
      seoDescription="JobHacki terms of service: membership terms, acceptable use, billing via Whop, cancellation, content ownership, and limitation of liability."
      kicker="TERMS OF SERVICE"
      title={<>The rules. <span className="bg-[#FF3B30] text-white px-2">Spelled out.</span></>}
      effectiveDate="June 11, 2026"
      accent="#0055FF"
      intro={<>These terms govern your use of jobhacki.com and the JobHacki membership, tools, and content (together, the &ldquo;Service&rdquo;). By creating an account or using the Service, you agree to them. If you don&apos;t agree, don&apos;t use the Service.</>}
      sections={[
        {
          title: "Membership and accounts",
          body: (
            <ul className="space-y-2 list-disc pl-5">
              <li>A free account unlocks the free tools (such as the Playbook Directory). A paid All-Access membership unlocks the paid tools.</li>
              <li>You must provide a valid email address and keep your login credentials to yourself. Your account is for you — one person per account.</li>
              <li>You&apos;re responsible for activity that happens under your account. If you think someone else has access to it, tell us right away.</li>
              <li>You must be at least 16 years old (or the age of digital consent where you live) to use the Service.</li>
            </ul>
          ),
        },
        {
          title: "Subscriptions and billing (via Whop)",
          body: (
            <ul className="space-y-2 list-disc pl-5">
              <li>Paid memberships are billed through <b>Whop</b>, our payment processor. The price and billing interval are shown at checkout before you pay.</li>
              <li>Subscriptions renew automatically at the end of each billing period until you cancel.</li>
              <li>Billing, payment disputes, and refunds are handled through Whop under its own terms and policies. We never see or store your card details.</li>
            </ul>
          ),
        },
        {
          title: "Cancellation",
          body: (
            <p>You can cancel your subscription at any time through your Whop account. Cancellation stops the next renewal; you keep paid access until the end of the billing period you&apos;ve already paid for. We don&apos;t do cancellation phone calls, retention mazes, or hidden steps.</p>
          ),
        },
        {
          title: "Acceptable use",
          body: (
            <>
              <p>Don&apos;t abuse the Service. Specifically, you agree not to:</p>
              <ul className="space-y-2 list-disc pl-5">
                <li>Share, resell, or redistribute your account, login, or any members-only content (playbooks, guides, tools) outside the Service.</li>
                <li>Scrape, bulk-download, or systematically copy the Service or its content.</li>
                <li>Reverse engineer, probe, or interfere with the Service&apos;s security or infrastructure.</li>
                <li>Use the Service for anything unlawful, fraudulent, or harmful to others.</li>
              </ul>
              <p>We may suspend or terminate accounts that violate these rules.</p>
            </>
          ),
        },
        {
          title: "Intellectual property",
          body: (
            <p>All content on the Service — playbooks, guides, tools, copy, design, and branding — is owned by JobHacki or its licensors and is protected by copyright and other IP laws. Your membership grants you a personal, non-exclusive, non-transferable license to use the content for your own learning and projects. It does not transfer ownership, and it doesn&apos;t allow you to republish or resell the content. Anything you build by following a playbook is yours.</p>
          ),
        },
        {
          title: "Third-party services and links",
          body: (
            <p>The Service links to and integrates with third-party services (for example Whop for payments, and external tools referenced in playbooks). Those services have their own terms, and we&apos;re not responsible for them. See our <a className="font-bold border-b-2 border-current" href="/disclaimer">Earnings Disclaimer</a> for how we handle external and affiliate links.</p>
          ),
        },
        {
          title: "No guarantees; disclaimers",
          body: (
            <>
              <p>The Service is provided <b>&ldquo;as is&rdquo;</b>. Playbooks and tools are educational — we don&apos;t guarantee any income, job outcome, or specific result. Read the <a className="font-bold border-b-2 border-current" href="/disclaimer">Earnings Disclaimer</a>; it&apos;s part of these terms.</p>
              <p>To the maximum extent permitted by law, we disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We don&apos;t promise the Service will be uninterrupted or error-free, though we work to keep it both.</p>
            </>
          ),
        },
        {
          title: "Limitation of liability",
          body: (
            <p>To the maximum extent permitted by law, JobHacki will not be liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenue, or data, arising from your use of the Service. Our total liability for any claim relating to the Service is limited to the amount you paid us in the twelve (12) months before the claim arose. Some jurisdictions don&apos;t allow certain limitations, so parts of this section may not apply to you.</p>
          ),
        },
        {
          title: "Termination",
          body: (
            <p>You can stop using the Service and delete your account at any time (see the <a className="font-bold border-b-2 border-current" href="/privacy">Privacy Policy</a> for data deletion). We may suspend or terminate accounts that violate these terms. Sections that by their nature should survive termination (IP, disclaimers, limitation of liability, governing law) survive it.</p>
          ),
        },
        {
          title: "Changes to these terms",
          body: (
            <p>We may update these terms as the Service evolves. If we do, we&apos;ll update the effective date above, and flag material changes on the site. Continuing to use the Service after a change means you accept the updated terms.</p>
          ),
        },
        {
          title: "Governing law",
          body: (
            <p>These terms are governed by the laws of the State of California, USA, without regard to its conflict-of-law rules. Any dispute will be resolved in the state or federal courts located in California, and you consent to their jurisdiction.</p>
          ),
        },
      ]}
    />
  );
}
